import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { useUpdateCandidate } from '@/hooks/use-candidates';
import { extractErrorMessage } from '@/lib/errors';
import { cn } from '@/lib/utils';
import { usePermission } from '@/lib/permissions';
import { isConsentLocked } from '../lib/consent-actions';
import type { CandidateCreator, CandidateDetail } from '@/types/candidates';
import { Pencil } from 'lucide-react';
import { useEffect, useState } from 'react';
import { EmployeeMultiSelect, type EmployeeOption } from './employee-multi-select';

interface CandidateResponsibleRecruitersCardProps {
  candidate: CandidateDetail;
}

function toOptions(list: CandidateCreator[]): EmployeeOption[] {
  return list.map((e) => ({
    id: e.id,
    firstName: e.firstName,
    lastName: e.lastName,
    avatarUrl: e.avatarUrl,
  }));
}

function sameMembers(a: EmployeeOption[], b: EmployeeOption[]): boolean {
  if (a.length !== b.length) return false;
  const aIds = new Set(a.map((e) => e.id));
  return b.every((e) => aIds.has(e.id));
}

export function CandidateResponsibleRecruitersCard({
  candidate,
}: CandidateResponsibleRecruitersCardProps) {
  // HW-1055: a candidate who revoked consent is read-only until anonymised.
  const canEdit = usePermission('recruitment.candidates.edit') && !isConsentLocked(candidate);
  const { mutateAsync } = useUpdateCandidate(candidate.id);
  const recruiters = candidate.responsibleRecruiters;

  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState<EmployeeOption[]>(toOptions(recruiters));
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!isEditing) setDraft(toOptions(recruiters));
  }, [recruiters, isEditing]);

  const enterEdit = () => {
    if (!canEdit || isSaving) return;
    setDraft(toOptions(recruiters));
    setError(null);
    setIsEditing(true);
  };

  const cancel = () => {
    setDraft(toOptions(recruiters));
    setError(null);
    setIsEditing(false);
  };

  const save = async () => {
    if (sameMembers(draft, toOptions(recruiters))) {
      setIsEditing(false);
      return;
    }
    setIsSaving(true);
    setError(null);
    try {
      await mutateAsync({ responsibleRecruiterIds: draft.map((e) => e.id) });
      setIsEditing(false);
    } catch (err) {
      setError(extractErrorMessage(err, 'Failed to update Responsible Recruiter'));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <section className="rounded-lg border border-border bg-background p-5">
      <header className="mb-3 flex items-center justify-between">
        <h2 className="text-base font-medium">Responsible Recruiter</h2>
        {!isEditing && canEdit && (
          <button
            type="button"
            onClick={enterEdit}
            className="inline-flex items-center gap-1 rounded px-2 py-1 text-xs text-muted-foreground hover:bg-muted/50 hover:text-foreground"
            aria-label="Edit Responsible Recruiter"
          >
            <Pencil className="h-3.5 w-3.5" />
            Edit
          </button>
        )}
      </header>

      {isEditing ? (
        <>
          <EmployeeMultiSelect value={draft} onChange={setDraft} autoFocus />
          <div className="mt-3 flex items-center justify-end gap-2">
            <Button size="sm" variant="ghost" onClick={cancel} disabled={isSaving}>
              Cancel
            </Button>
            <Button size="sm" onClick={save} disabled={isSaving}>
              {isSaving ? 'Saving…' : 'Save'}
            </Button>
          </div>
          {error && <p className="mt-2 text-xs text-destructive">{error}</p>}
        </>
      ) : recruiters.length === 0 ? (
        <button
          type="button"
          disabled={!canEdit}
          onClick={enterEdit}
          className={cn(
            'w-full rounded px-2 py-1 text-left text-sm text-muted-foreground',
            canEdit && 'cursor-text hover:bg-muted/50',
          )}
        >
          No responsible recruiter assigned
        </button>
      ) : (
        <ul className="space-y-2">
          {recruiters.map((r) => (
            <li key={r.id} className="flex items-center gap-3">
              <Avatar className="h-8 w-8 shrink-0 rounded-full">
                {r.avatarUrl ? (
                  <AvatarImage src={r.avatarUrl} alt={`${r.firstName} ${r.lastName}`} />
                ) : null}
                <AvatarFallback className="rounded-full text-[10px]">
                  {`${r.firstName[0] ?? ''}${r.lastName[0] ?? ''}`.toUpperCase() || '?'}
                </AvatarFallback>
              </Avatar>
              <span className="min-w-0 truncate text-sm">
                {r.firstName} {r.lastName}
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
